import {
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";

import keys from "queries/keys";
import { pathFor as pathForProject } from "queries/projects";
import ocClient from "services/ocClient";
import { Project } from "types/entities";

import resourceOperations from "../resourceOperations";

const operation = resourceOperations({
  basePath: "/api/admin/projects",
  baseKey: ["admin", "projects"],
  dataKey: "project",
  versioned: false,
});

export const useExplainProject = (id, { identifier, rawExpression }, opts = {}) => {
  const path = new URLSearchParams({ identifier, rawExpression }).toString();
  return operation.useRead([id, `explain?${path}`], {
    params: { identifier, raw_expression: rawExpression },
    ...opts,
  });
};

export const useFillAnswers = (id: number, opts = {}) =>
  operation.useUpdate([id, "fill_answers"], {
    dataKey: null,
    invalidates: keys.projects(id),
    ...opts,
  });

interface ProjectResponse {
  projects: Project[];
}

type Owner = {
  id: number;
  email: string;
};

interface OwnerResponse {
  data: {
    id: number;
  };
}

export const useMutateOwner = (
  opts: Omit<UseMutationOptions<OwnerResponse, Error, Owner>, "mutationFn"> = {},
) => {
  const queryClient = useQueryClient();
  return useMutation<OwnerResponse, Error, Owner>(
    ({ id, email }: Owner) => ocClient.put(`/api/admin/projects/${id}/change_owner`, { email }),
    {
      ...opts,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(pathForProject(data.data.id));
        if (opts.onSuccess) {
          opts.onSuccess(data, variables, context);
        }
      },
    },
  );
};

interface SpecialEventsProjectsParams {
  hideRejected?: boolean;
  hideUnsubmitted?: boolean;
  startDate?: string;
  endDate?: string;
}

export const useSpecialEventsProjects = ({
  hideRejected,
  hideUnsubmitted,
  startDate,
  endDate,
  ...opts
}: SpecialEventsProjectsParams &
  Omit<UseQueryOptions<Project[], Error>, "queryKey" | "queryFn"> = {}) => {
  const params = {
    hideUnsubmitted,
    hideRejected,
    "dateRange[startDate]": startDate,
    "dateRange[endDate]": endDate,
  };
  return useQuery<Project[], Error>({
    queryKey: ["admin", "projects", "special_events", params],
    queryFn: async () => {
      const {
        data: { projects },
      } = await ocClient.get<ProjectResponse>("/api/admin/projects/special_events", { params });
      return projects;
    },
    ...opts,
  });
};

const BATCH_SIZE = 100; // FIXME: how does one know this matches what the API returns? We should probably use cursors instead of `page * magic-number`

// TODO: Restrict available filters
interface ProjectFilters {
  [key: string]: unknown;
}

export const useInfiniteProjectsList = (filters: ProjectFilters = {}, opts: any = {}) =>
  useInfiniteQuery<ProjectResponse, Error>({
    queryKey: ["admin", "projects", "list", filters],
    queryFn: async ({ pageParam = 0 }) => {
      const offset = pageParam * BATCH_SIZE;
      const { data } = await ocClient.get<ProjectResponse>("/api/admin/projects", {
        params: { ...filters, offset },
      });
      return data;
    },
    initialPageParam: 0,
    getNextPageParam: (_lastGroup, groups) => groups.length,
    refetchOnWindowFocus: false,
    staleTime: 500,
    ...opts,
  });
